import { addToast } from '@xefi/x-react/toast';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const useToasts = () => {
   const { t } = useTranslation();

   const commonOptions = useMemo(
      () => ({
         timeout: 5000,
         shouldShowTimeoutProgress: true,
         variant: 'flat' as const,
      }),
      [],
   );

   const shownToastsRef = useRef<Set<string>>(new Set());
   const toastTimersRef = useRef<Map<string, number>>(new Map());

   const addSuccessToast = useCallback(
      (descriptionArg?: string | null, id?: string | null) => {
         const description = descriptionArg ?? t('success', { ns: 'common' });
         const idKey = id ?? description;

         if (
            shownToastsRef.current.has(idKey) ||
            shownToastsRef.current.has(description)
         ) {
            return;
         }

         shownToastsRef.current.add(idKey);

         if (description !== idKey) shownToastsRef.current.add(description);

         const timeoutMs = (commonOptions.timeout ?? 5000) + 50;
         const timerId = window.setTimeout(() => {
            shownToastsRef.current.delete(idKey);
            if (description !== idKey)
               shownToastsRef.current.delete(description);
            toastTimersRef.current.delete(idKey);
         }, timeoutMs);
         toastTimersRef.current.set(idKey, timerId);

         addToast({
            color: 'success',
            description: description,
            ...commonOptions,
         });
      },
      [commonOptions, t],
   );

   const addErrorToast = useCallback(
      (descriptionArg?: string | null, id?: string | null) => {
         const description = descriptionArg ?? t('error', { ns: 'common' });
         const idKey = id ?? description;

         if (
            shownToastsRef.current.has(idKey) ||
            shownToastsRef.current.has(description)
         ) {
            return;
         }

         shownToastsRef.current.add(idKey);
         if (description !== idKey) shownToastsRef.current.add(description);

         const timeoutMs = (commonOptions.timeout ?? 5000) + 50;
         const timerId = window.setTimeout(() => {
            shownToastsRef.current.delete(idKey);
            if (description !== idKey)
               shownToastsRef.current.delete(description);
            toastTimersRef.current.delete(idKey);
         }, timeoutMs);
         toastTimersRef.current.set(idKey, timerId);

         addToast({
            color: 'danger',
            description: description,
            ...commonOptions,
         });
      },
      [commonOptions, t],
   );

   useEffect(() => {
      const timers = toastTimersRef.current;
      const shown = shownToastsRef.current;

      return () => {
         for (const timerId of timers.values()) {
            clearTimeout(timerId);
         }
         timers.clear();
         shown.clear();
      };
   }, []);

   return { addSuccessToast, addErrorToast };
};
